import React, { FC } from "react";

import dayjs from "dayjs";

import { PaymentStatuses, type Resale, ResaleStatuses, SortDirection, useListGroupedResalesQuery } from "@api/graphql";
import { GroupedResales } from "@components/grouped_resales/GroupedResales";
import { ResalesAccordionHeader } from "@components/grouped_resales/ResaleAccordionHeader";
import { ProtectedComponent, useIsAuthUserAllowed } from "@components/permission_control/ProtectedComponent";
import type { TableFilterInput } from "@components/table/TableFilter";
import { useAuthenticationContext } from "@context/authentication";
import { ExternalRoles, Role } from "@roles";
import type { FilterByTyped, SortByTyped } from "@util/index";
import type { ResaleGroup } from "@util/resale";

import styles from "./HoldPeriodResale.module.scss";

interface OnHoldResalesAccordionHeaderProps {
  resales: Resale[];
  isExternalUser: boolean;
  resaleCount: number;
}

export const OnHoldResalesAccordionHeader: FC<OnHoldResalesAccordionHeaderProps> = ({
  resales,
  isExternalUser,
  resaleCount,
}) => {
  return <ResalesAccordionHeader resales={resales} isExternalUser={isExternalUser} resaleCount={resaleCount} />;
};

export const HoldPeriodResale = () => {
  const { auth } = useAuthenticationContext();
  const filter_by: FilterByTyped<Resale>[] = [{ field: "opt_out_expiry", gte: dayjs(new Date()).format("YYYY-MM-DD") }];

  const sort_by: SortByTyped<Resale> = {
    field: "reported_on",
    direction: SortDirection.DESCENDING,
  };
  const isExternalUser = useIsAuthUserAllowed(ExternalRoles);
  const getAccordionHeader = ({ resales }: ResaleGroup) => {
    return (
      <ProtectedComponent
        requiredGroups={[Role.VisualArtistAdmin, Role.MemberServices, Role.Data, Role.ResaleReporter]}
        auth={auth}
      >
        <OnHoldResalesAccordionHeader resales={resales} isExternalUser={isExternalUser} resaleCount={resales.length} />
      </ProtectedComponent>
    );
  };
  const resaleStatusFilterOptions: TableFilterInput[] = [
    {
      option: ResaleStatuses.CONFIRMED_ELIGIBLE,
      defaultValue: true,
    },
    {
      option: ResaleStatuses.PROVISIONALLY_ELIGIBLE,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.CONFIRMED_INELIGIBLE,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.PROVISIONALLY_INELIGIBLE,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.RESEARCH_REQUIRED,
      defaultValue: true,
    },
    {
      option: ResaleStatuses.OPTED_OUT,
      defaultValue: false,
    },
    {
      option: ResaleStatuses.INVALID,
      defaultValue: false,
    },
  ];

  const paymentStatusFilterOptions: TableFilterInput[] = [
    {
      option: PaymentStatuses.AMP_INVOICE_PAID,
      defaultValue: false,
    },
    {
      option: PaymentStatuses.AMP_INVOICE_REQUESTED,
      defaultValue: false,
    },
    {
      option: PaymentStatuses.ARTIST_PAID,
      defaultValue: false,
    },
    {
      option: PaymentStatuses.ARTIST_PAYMENT_REQUESTED,
      defaultValue: false,
    },
    {
      option: PaymentStatuses.CREDIT_NOTE_ISSUED,
      defaultValue: false,
    },
    {
      option: PaymentStatuses.CREDIT_NOTE_REQUESTED,
      defaultValue: false,
    },
    {
      option: PaymentStatuses.HOLD_PERIOD,
      defaultValue: true,
    },
    {
      option: PaymentStatuses.INVOICE_REQUEST_SENT,
      defaultValue: false,
    },
    {
      option: PaymentStatuses.NOT_APPLICABLE,
      defaultValue: false,
    },
    {
      option: PaymentStatuses.PENDING,
      defaultValue: false,
    },
  ];

  return (
    <>
      <GroupedResales
        queryFunction={useListGroupedResalesQuery}
        getQueryVariables={(page) => ({
          input: { page, limit: 10, filter_by, sort_by },
        })}
        updateQueryVariablesWithFilter={({ input: { page, limit, filter_by, sort_by } }, new_filter_by) => ({
          input: {
            page,
            limit,
            filter_by: filter_by.concat(new_filter_by ? new_filter_by : []),
            sort_by,
          },
        })}
        getResaleGroupsFromQueryData={(data) => data.listGroupedResales.results}
        getPaginatedOutputFromQueryData={(data) => data.listGroupedResales}
        getAccordionHeader={getAccordionHeader}
        resaleStatusFilterOptions={resaleStatusFilterOptions}
        paymentStatusFilterOptions={paymentStatusFilterOptions}
        sourcePage="HoldPeriod"
        customTableStyleClassName={styles["customized-column-width"]}
      ></GroupedResales>
    </>
  );
};
